import React, { FC, memo, ReactNode, useEffect } from 'react';
import { Modal } from 'shared/ui/Modal';
import { Button } from 'shared/ui/Button';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { WarningPing } from '../WarningPing';
import LockOpenTooLong from 'shared/assets/sounds/LockOpenTooLong.mp3';

interface ConfirmModalProps {
  isOpened: boolean;
  title?: string;
  description?: ReactNode;
  onOk?: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
  isConfirmDisabled?: boolean;
  showWarningAnimation?: boolean;
  playSound?: boolean;
}

export const ConfirmModal: FC<ConfirmModalProps> = memo((props) => {
  const { isOpened, title, description, onOk, onCancel, isLoading, isConfirmDisabled, showWarningAnimation = false, playSound } = props;

  const { t } = useAppTranslation('common');

  useEffect(() => {
    if (isOpened && playSound) {
      const audio = new Audio(LockOpenTooLong);
      audio.play().catch(() => {
        console.warn('Failed to play notification sound');
      });
    }
  }, [isOpened, playSound]);

  const actions = (): ReactNode => (
    <div className="flex space-x-3">
      {onCancel && (
        <Button containerClassName="w-full" theme="secondary" isLoading={isLoading} onClick={onCancel}>
          {t('Cancel')}
        </Button>
      )}
      {onOk && (
        <Button containerClassName="w-full" isLoading={isLoading} isDisabled={isConfirmDisabled} onClick={onOk}>
          {t('Confirm')}
        </Button>
      )}
    </div>
  );

  return (
    <Modal className="max-w-[400px]" isOpen={isOpened} onClose={onCancel} closeIcon={false} footer={actions()}>
      <div className="flex items-start">
        {showWarningAnimation && (
          <div className="mb-2">
            <WarningPing type="warning" />
          </div>
        )}
        <div>
          <div className="font-semibold text-xl my-1 font-tektur">{title}</div>
          <div className="text-primaryLight font-normal">{description}</div>
        </div>
      </div>
    </Modal>
  );
});
